"use client";
import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../config/api";
import { Button } from "../../../components/ui/button";
import { Plus, ClipboardList } from "lucide-react";
import { ListPaginationFooter } from "../../../components/ListPaginationFooter";
import { Estimate } from "../types/estimate";
import { estimateToOrderShape, estimateToOrderShapeForConvert } from "../types/estimate";
import { EstimateCard } from "./EstimateCard";
import { OrderForm } from "../orders/OrderForm";
import { coerceLineProducts } from "../orders/OrderProductsDetailsSection";
import { PANEL_DATA_CHANGED, type PanelDataChange } from "../../../utils/panelDataSync";
import { Order } from "../types/order";
import { formatRoleLabel, isAdminRole } from "../../../utils/roles";

export const EstimatesPage: React.FC = () => {
  const [estimates, setEstimates] = useState<Estimate[]>([]);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [selected, setSelected] = useState<Estimate | null>(null);
  const [convertEstimateId, setConvertEstimateId] = useState<string | null>(null);
  const [orderFormOpen, setOrderFormOpen] = useState(false);
  const [convertPrefill, setConvertPrefill] = useState<Order | null>(null);
  const [createdBy, setCreatedBy] = useState("");
  const [customer, setCustomer] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [appliedFilters, setAppliedFilters] = useState({
    createdBy: "",
    customer: "",
    fromDate: "",
    toDate: "",
  });
  const [users, setUsers] = useState<any[]>([]);
  const [customers, setCustomers] = useState<any[]>([]);
  const [filtersLoaded, setFiltersLoaded] = useState(false);

  const { authToken = "", id = "", role = "", username = "" } = useSelector(
    (state: any) => state.loginData || {}
  );
  const isAdmin = isAdminRole(role);
  const isDealer = String(role || "").trim().toLowerCase() === "dealer";
  const isStaff = String(role || "").trim().toLowerCase() === "employee";
  const panelUserRole =
    role === "admin" ? "admin" : isDealer ? "dealer" : "employee";
  const maxResults = 10;

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/users`, {
        params: { firstResults: 0, maxResults: "all", role: "creator" },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setUsers(res.data.users || []);
    } catch (e) {
      console.error("Error fetching users:", e);
    }
  };

  const fetchCustomers = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/users`, {
        params: { firstResults: 0, maxResults: "all", role: "all" },
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setCustomers(res.data.users || []);
    } catch (e) {
      console.error("Error fetching customers:", e);
    }
  };

  const ensureFilterOptions = async () => {
    if (filtersLoaded || !id || !authToken) return;
    await Promise.all([fetchUsers(), fetchCustomers()]);
    setFiltersLoaded(true);
  };

  const fetchEstimates = useCallback(
    async (pageNum = page) => {
      if (!id || !authToken) return;
      setLoading(true);
      try {
        const first = (pageNum - 1) * maxResults;
        const params: Record<string, string | number> = { first, max: maxResults };
        if (appliedFilters.createdBy.trim()) params.createdBy = appliedFilters.createdBy.trim();
        if (appliedFilters.customer.trim()) params.customer = appliedFilters.customer.trim();
        if (appliedFilters.fromDate.trim()) params.fromDate = appliedFilters.fromDate.trim();
        if (appliedFilters.toDate.trim()) params.toDate = appliedFilters.toDate.trim();

        const res = await axios.get(`${API_BASE_URL}account/${id}/estimates`, {
          params,
          headers: { Authorization: `Bearer ${authToken}` },
        });
        setEstimates(res.data?.estimates || []);
        setTotalCount(res.data?.totalCount || 0);
      } catch (e) {
        console.error("Failed to load estimates", e);
        setEstimates([]);
      } finally {
        setLoading(false);
      }
    },
    [authToken, id, appliedFilters]
  );

  useEffect(() => {
    fetchEstimates(page);
  }, [fetchEstimates, page]);

  useEffect(() => {
    const onPanelDataChanged = (event: Event) => {
      const detail = (event as CustomEvent<PanelDataChange>).detail;
      if (detail?.quotations) fetchEstimates(page);
    };
    window.addEventListener(PANEL_DATA_CHANGED, onPanelDataChanged);
    return () => window.removeEventListener(PANEL_DATA_CHANGED, onPanelDataChanged);
  }, [fetchEstimates, page]);

  useEffect(() => {
    if (id && authToken) ensureFilterOptions();
  }, [id, authToken]);

  const totalPages = Math.ceil(totalCount / maxResults) || 1;

  const handlePrev = () => {
    if (page > 1) setPage(page - 1);
  };

  const handleNext = () => {
    if (page < totalPages) setPage(page + 1);
  };

  const applyFilters = () => {
    setAppliedFilters({ createdBy, customer, fromDate, toDate });
    setPage(1);
  };

  const clearFilters = () => {
    setCreatedBy("");
    setCustomer("");
    setFromDate("");
    setToDate("");
    setAppliedFilters({ createdBy: "", customer: "", fromDate: "", toDate: "" });
    setPage(1);
  };

  const handleEstimateSave = async (payload: Record<string, unknown>) => {
    const body = { ...payload, createdBy: payload.createdBy || username || id };
    if (payload.id) {
      await axios.put(`${API_BASE_URL}account/${id}/estimate/update`, body, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
    } else {
      await axios.post(`${API_BASE_URL}account/${id}/estimate/create`, body, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
    }
    fetchEstimates(page);
    setShowForm(false);
    setSelected(null);
  };

  const openEdit = async (est: Estimate) => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/estimate/${est.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      setConvertEstimateId(null);
      const data = res.data as Estimate;
      setSelected({
        ...data,
        products: coerceLineProducts(data?.products) as Estimate["products"],
      });
      setShowForm(true);
    } catch {
      alert("Could not load quotation");
    }
  };

  const openConvert = async (est: Estimate) => {
    try {
      const res = await axios.get(`${API_BASE_URL}account/${id}/estimate/${est.id}`, {
        headers: { Authorization: `Bearer ${authToken}` },
      });
      const full = res.data as Estimate;
      setShowForm(false);
      setSelected(null);
      setConvertEstimateId(full.id);
      setConvertPrefill(estimateToOrderShapeForConvert(full) as unknown as Order);
      setOrderFormOpen(true);
    } catch {
      alert("Could not load quotation");
    }
  };

  const closeOrderConvertForm = () => {
    setOrderFormOpen(false);
    setConvertEstimateId(null);
    setConvertPrefill(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="p-3 rounded-xl bg-emerald-100">
            <ClipboardList className="text-emerald-700" size={28} />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Quotations</h1>
            <p className="text-sm text-gray-500">Create quotations and convert to orders when ready</p>
          </div>
        </div>
        <Button
          onClick={() => {
            setConvertEstimateId(null);
            setSelected(null);
            setShowForm(true);
          }}
          className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
        >
          <Plus className="mr-2 h-4 w-4" /> New quotation
        </Button>
      </div>

      <div className="bg-white rounded-xl border-2 border-emerald-200 p-4 flex flex-col gap-3 lg:flex-row lg:flex-wrap xl:flex-nowrap lg:items-end">
          {isAdmin && (
            <div className="flex flex-col w-full lg:flex-1 lg:min-w-[140px]">
              <label className="text-sm font-medium text-gray-600 mb-1">Created By</label>
              <select
                value={createdBy}
                onChange={(e) => setCreatedBy(e.target.value)}
                className="border border-gray-300 rounded-lg px-3 h-10 text-sm bg-white w-full"
              >
                <option value="">All</option>
                {users.map((user) => (
                  <option key={user.id} value={user.username}>
                    {user.username}
                    {user.role ? ` (${formatRoleLabel(user.role)})` : ""}
                  </option>
                ))}
              </select>
            </div>
          )}
          <div className="flex flex-col w-full lg:flex-1 lg:min-w-[140px]">
            <label className="text-sm font-medium text-gray-600 mb-1">Customer</label>
            <select
              value={customer}
              onChange={(e) => setCustomer(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 h-10 text-sm bg-white w-full"
            >
              <option value="">All</option>
              {customers.map((user) => (
                <option key={user.id} value={String(user.id)}>
                  {user.companyName || user.username}
                  {user.role ? ` (${formatRoleLabel(user.role)})` : ""}
                </option>
              ))}
            </select>
          </div>
          <div className="flex flex-col w-full sm:w-[calc(50%-0.375rem)] lg:w-[150px] lg:flex-none">
            <label className="text-sm font-medium text-gray-600 mb-1">From</label>
            <input
              type="date"
              value={fromDate}
              onChange={(e) => setFromDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 h-10 text-sm bg-white w-full"
            />
          </div>
          <div className="flex flex-col w-full sm:w-[calc(50%-0.375rem)] lg:w-[150px] lg:flex-none">
            <label className="text-sm font-medium text-gray-600 mb-1">To</label>
            <input
              type="date"
              value={toDate}
              onChange={(e) => setToDate(e.target.value)}
              className="border border-gray-300 rounded-lg px-3 h-10 text-sm bg-white w-full"
            />
          </div>
          <div className="w-full lg:w-auto lg:flex-none flex flex-row gap-2">
            <Button
              onClick={applyFilters}
              className="bg-emerald-600 hover:bg-emerald-700 text-white h-10 px-5 flex-1 sm:flex-none whitespace-nowrap"
            >
              Apply
            </Button>
            <Button
              variant="outline"
              className="h-10 px-5 flex-1 sm:flex-none whitespace-nowrap"
              onClick={clearFilters}
            >
              Clear
            </Button>
          </div>
        </div>

      {loading ? (
        <p className="text-gray-500">Loading…</p>
      ) : estimates.length === 0 ? (
        <p className="text-gray-500">No quotations found.</p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {estimates.map((est) => (
            <EstimateCard
              key={est.id}
              estimate={est}
              role={panelUserRole}
              refresh={() => fetchEstimates(page)}
              onEdit={openEdit}
              onConvert={openConvert}
            />
          ))}
        </div>
      )}

      <ListPaginationFooter
        page={page}
        totalPages={totalPages}
        totalCount={totalCount}
        currentCount={estimates.length}
        itemLabel="quotations"
        accent="emerald"
        onPrev={handlePrev}
        onNext={handleNext}
      />

      <OrderForm
        open={showForm}
        onClose={() => {
          setShowForm(false);
          setSelected(null);
          setConvertEstimateId(null);
        }}
        refresh={() => fetchEstimates(page)}
        userRole={panelUserRole}
        userId={String(id)}
        variant="estimate"
        onEstimateSave={handleEstimateSave}
        existingOrder={
          selected ? (estimateToOrderShape(selected) as unknown as Order) : null
        }
      />

      <OrderForm
        open={orderFormOpen}
        onClose={closeOrderConvertForm}
        refresh={() => fetchEstimates(page)}
        userRole={panelUserRole}
        userId={String(id)}
        variant="order"
        convertFromEstimateId={convertEstimateId}
        existingOrder={convertPrefill}
      />
    </div>
  );
};
